import { Button, Typography } from "@povio/ui";
import { cx } from "class-variance-authority";
import { useRouter } from "next/router";
import { useEffect, useMemo } from "react";
import { PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer } from "recharts";

import ProfileSectionCard from "@/components/home/sections/ProfileSectionCard";
import { SkeletonWrapper } from "@/components/onboarding/ui/Skeleton";
import { DemoClickWrapper } from "@/components/shared/demo/DemoClickWrapper";
import { TourSelectors } from "@/config/tour.config";
import { AuthContext } from "@/data/auth/auth.context";
import { CommonModels } from "@/data/common/common.models";
import { ProfileQueries } from "@/data/profile/profile.queries";
import { ProfileAnalyticsQueries } from "@/data/profileAnalytics/profileAnalytics.queries";
import { PublicProfileQueries } from "@/data/publicProfile/publicProfile.queries";
import { useDemoAlternativeQuery } from "@/hooks/demo/useDemoAlternativeQuery";
import { useSessionAnimation } from "@/hooks/useSessionAnimation";
import { DemoContext } from "@/providers/demo/DemoContext";

const MAX_DIAGRAM_SKILLS = 6; // Skills shown on diagram

const PADDED_VALUE_TITLE = "paddedvalue";

interface IProps {
  hideLabel?: boolean;
  isAnalytics?: boolean;
  guestMode?: boolean;
  isProcessingWorthData?: boolean;
  isPublic?: boolean;
  onEditSectionClicked?: () => void;
  skills?: CommonModels.SkillDto[];
  skillIndustry?: CommonModels.ProfileBlsSkillIndustryResponseDto | null;
  className?: string;
  guestName?: string;
}
const CustomTick = (props: any) => {
  const { payload, x, y, index } = props;
  const words = payload.value.split(" ");
  const fontSize = 12;
  const width = 700;
  const isLeftSide = x < width / 4;
  const isCenter = Math.abs(x - width / 4) < 15;
  let newY = y - fontSize;
  // move the top text so that is above the graph
  if (index === 0 && words.length > 1) {
    newY -= (words.length - 1) * fontSize;
  }
  return (
    <text
      x={x}
      y={newY}
      // eslint-disable-next-line no-nested-ternary
      textAnchor={isCenter ? "middle" : isLeftSide ? "end" : "start"}
      dominantBaseline="middle"
      fontSize={fontSize}
      fontFamily="var(--font-secondary)"
      fill="#DCDCDC"
      className="text-neutral-100 capitalize"
    >
      {words.map((word: string, wordIndex: number) => (
        // eslint-disable-next-line react/no-array-index-key
        <tspan
          x={x}
          dy={fontSize}
          key={wordIndex}
        >
          {word.includes(PADDED_VALUE_TITLE) ? "" : word}
          <title>{payload.value}</title>
        </tspan>
      ))}
    </text>
  );
};

export const SkillsRadarChart = ({
  topSkills,
  sessionKey = "skills-radar-chart",
}: {
  topSkills?: CommonModels.SkillScoreDto[];
  sessionKey?: string;
}) => {
  const shouldAnimate = useSessionAnimation(sessionKey);

  const data = useMemo(() => {
    return topSkills?.map((topSkill) => ({
      title: topSkill.name,
      value: topSkill.profileScore,
    }));
  }, [topSkills]);

  const industryData = useMemo(() => {
    return topSkills?.map((topSkill) => ({
      title: topSkill.name,
      value: topSkill.industryScore ?? 0,
    }));
  }, [topSkills]);

  const mergedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const defaultIndustryValue = data.length === 1 ? undefined : 0;

    const mappedData = data.map((item, index) => ({
      ...item,
      industryValue: industryData?.[index]?.value ?? defaultIndustryValue,
    }));
    const paddedLength = MAX_DIAGRAM_SKILLS - mappedData.length;
    if (paddedLength > 0) {
      for (let i = 0; i < paddedLength; i++) {
        mappedData.push({ title: `${PADDED_VALUE_TITLE}_${i}`, value: 0, industryValue: defaultIndustryValue });
      }
    }
    return mappedData;
  }, [data, industryData]);

  const strokeLinecap = topSkills?.length === 1 ? "round" : "butt";
  const strokeLinejoin = topSkills?.length === 1 ? "round" : "miter";

  const chartOuterRadius = topSkills?.[4]?.name.length && topSkills?.[4]?.name.length > 20 ? "60%" : "70%";

  return (
    <div
      className={cx(
        "flex grow items-center justify-center",
        shouldAnimate ? "animate-radar-chart-appear opacity-0" : "opacity-100",
      )}
    >
      {!!mergedData?.length && (
        <ResponsiveContainer
          width="100%"
          height="100%"
          className="min-h-[300px]"
        >
          <RadarChart
            startAngle={150}
            endAngle={-210}
            cx="50%"
            cy="50%"
            outerRadius={chartOuterRadius}
            data={mergedData}
            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          >
            <PolarGrid stroke="#A0A0A0" />
            <PolarRadiusAxis
              className="hidden"
              domain={[0, 100]}
              tick={false}
            />

            <Radar
              name="industryValue"
              dataKey="industryValue"
              stroke="#1A3891"
              fill="RGBA(59, 130, 246, 0.3)"
              strokeWidth={topSkills?.length === 1 ? 10 : 1}
              strokeLinecap={strokeLinecap}
              strokeLinejoin={strokeLinejoin}
              dot={false}
              style={{ mixBlendMode: "hard-light" }}
            />
            <Radar
              name="value"
              dataKey="value"
              stroke="#FFEA01"
              fill="RGBA(255, 234, 1, 0.3)"
              strokeWidth={topSkills?.length === 1 ? 3 : 1}
              strokeLinecap={strokeLinecap}
              strokeLinejoin={strokeLinejoin}
              dot={false}
              style={{ mixBlendMode: "hard-light" }}
            />

            <PolarAngleAxis
              dataKey="title"
              tick={CustomTick}
            />
          </RadarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

const ProfileKeySkillSetSection = ({
  hideLabel = false,
  isAnalytics = false,
  guestMode = false,
  isProcessingWorthData = false,
  isPublic = false,
  onEditSectionClicked,
  skills,
  skillIndustry,
  className,
  guestName,
}: IProps) => {
  const router = useRouter();
  const { demoUserId } = DemoContext.useDemo();
  const { user } = AuthContext.useAuth();
  const profileName = user?.user.name;
  const userName = profileName?.split(" ").shift() ?? "User";

  const profileUserId = router.query.userId as string;
  const { data: profileSkills, refetch: refetchProfileSkills } = useDemoAlternativeQuery(
    {
      query: ProfileQueries.useGetBlsSkillIndustry,
      options: {
        enabled: !isAnalytics && !isPublic && !guestMode,
      },
    },
    {
      query: PublicProfileQueries.useGetBlsSkillIndustry,
      params: [{ userId: isPublic ? profileUserId : demoUserId }],
    },
  );

  const { data: publicSkillsData } = PublicProfileQueries.useGetBlsSkillIndustry(
    { userId: profileUserId },
    { enabled: isPublic },
  );

  const { data: analyticsSkills, refetch: refetchAnalyticsSkills } = useDemoAlternativeQuery(
    {
      query: ProfileAnalyticsQueries.useGetBlsSkillIndustry,
      options: {
        enabled: isAnalytics && !guestMode,
      },
    },
    {
      query: PublicProfileQueries.useGetBlsSkillIndustry,
      params: [{ userId: demoUserId }],
    },
  );

  const topSkills = useMemo(() => {
    if (guestMode) {
      return skillIndustry?.data;
    }

    if (isPublic) {
      return publicSkillsData?.data.map((skill) => ({
        name: skill.name,
        profileScore: skill.profileScore,
      }));
    }
    if (isAnalytics) {
      return analyticsSkills?.data;
    }

    return profileSkills?.data.map((skill) => ({
      name: skill.name,
      profileScore: skill.profileScore,
    }));
  }, [
    skillIndustry?.data,
    guestMode,
    isAnalytics,
    isPublic,
    analyticsSkills?.data,
    publicSkillsData?.data,
    profileSkills?.data,
  ]);

  const topSkillsStatus = guestMode
    ? skillIndustry?.status
    : isAnalytics
      ? analyticsSkills?.status
      : profileSkills?.status;

  const tooltipText = useMemo(() => {
    if (guestMode) {
      return `${guestName} skills relative to their peers.`;
    }
    return "Your skills relative to your industry.";
  }, [guestMode, guestName]);

  useEffect(() => {
    if (guestMode) {
      return;
    }
    // When skills prop changes (from parent), refetch the BLS data
    // regardless of polling state
    if (!isPublic && !isAnalytics && skills && skills !== undefined) {
      refetchProfileSkills();
    }
    if (!isPublic && isAnalytics && skills && skills !== undefined) {
      refetchAnalyticsSkills();
    }
    // Only run when skills change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skills, guestMode]);

  useEffect(() => {
    if (isPublic || guestMode) {
      return;
    }
    if (isAnalytics) {
      refetchAnalyticsSkills();
    } else {
      refetchProfileSkills();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnalytics, isPublic, guestMode]);

  if (
    ((topSkillsStatus === CommonModels.StatusEnum.processing ||
      topSkillsStatus === CommonModels.StatusEnum.enqueued ||
      topSkillsStatus === CommonModels.StatusEnum.processing_full) &&
      !isPublic) ||
    (guestMode && isProcessingWorthData)
  ) {
    return (
      <SkeletonWrapper
        containerClassName="w-full flex-grow-0"
        className="h-[480px]"
      >
        <div className="p-4 text-text-default-3">
          <Typography
            size="title-3"
            variant="prominent-1"
          >
            Skills diagram
          </Typography>
        </div>
      </SkeletonWrapper>
    );
  }

  return (
    <div
      className={cx(
        "relative dd:min-h-auto min-h-50 w-full",
        isAnalytics && "h-full max-h-[480px]",
        topSkills?.length === 0 && isAnalytics && "h-full",
        className,
      )}
    >
      {topSkills?.length === 0 && (
        <div className="absolute top-0 right-0 bottom-0 left-0 z-20 flex w-full flex-col items-center justify-center gap-6 rounded-xl p-4 backdrop-blur-6">
          <Typography
            size="label-1"
            className="max-w-[300px] text-center text-text-default-3"
          >
            You have no skills added to your profile. Update 6 skills to see how you compare with your industry.
          </Typography>
          {!guestMode && !isPublic && onEditSectionClicked && (
            <DemoClickWrapper>
              <Button
                size="s"
                width="hug"
                color="secondary"
                onPress={onEditSectionClicked}
              >
                Add Skills
              </Button>
            </DemoClickWrapper>
          )}
        </div>
      )}
      <ProfileSectionCard
        title="Skills Diagram"
        titleSize="title-3"
        titleId={TourSelectors.SkillsDiagramSection}
        tooltip={tooltipText}
        isPublic={isPublic}
        cardClassName={cx("w-auto", topSkills?.length === 0 && isAnalytics && "h-full", isAnalytics && "h-full")}
        className="grow items-start justify-center gap-0"
        containerClassName="flex flex-col gap-2 p-4!"
      >
        <div className="flex w-full grow flex-col">
          {topSkills?.length! > 0 && !hideLabel && (
            <div className="flex w-fit flex-col">
              {(isAnalytics || guestMode) && (
                <div className="flex w-full items-center gap-2">
                  <div className="flex size-10 items-center justify-center">
                    <div className="h-3-5 w-3-5 rounded-full bg-text-neutral-2" />
                  </div>
                  <Typography
                    size="body-4"
                    className="text-text-default-2"
                  >
                    Industry
                  </Typography>
                </div>
              )}
              <div className="flex w-full items-center gap-2">
                <div className="flex size-10 items-center justify-center">
                  <div className="h-3-5 w-3-5 rounded-full bg-interactive-text-primary-idle" />
                </div>
                <Typography
                  size="body-4"
                  className="text-text-default-2"
                >
                  {guestMode ? (guestName ?? "Guest") : userName}
                </Typography>
              </div>
            </div>
          )}
          <SkillsRadarChart
            topSkills={topSkills}
            sessionKey="portfolio-skills-radar-chart"
          />
        </div>
      </ProfileSectionCard>
    </div>
  );
};

export default ProfileKeySkillSetSection;
